import React, { useState, Fragment } from 'react';
import { FunnelMetrics, FunnelStep, OpportunityRecord } from '../../types/throughput';
import { Card } from '../ui/Card';
import { ArrowRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
interface ThroughputFunnelProps {
  metrics: FunnelMetrics[];
  opportunities: OpportunityRecord[];
}
const KEY_STEPS: FunnelStep[] = ['Created', 'Converted', 'App Out', 'App Completed', 'Submitted to UW', 'Closing Scheduled', 'Funded'];
const STEP_LABELS: Partial<Record<FunnelStep, string>> = {
  Created: 'Claimed',
  'Closing Scheduled': 'CTC'
};
export function ThroughputFunnel({
  metrics,
  opportunities
}: ThroughputFunnelProps) {
  const [selectedStep, setSelectedStep] = useState<FunnelStep | null>(null);
  const keyMetrics = metrics.filter(m => KEY_STEPS.includes(m.step));
  // Calculate quality breakdown for Submitted to UW
  const submittedOpps = opportunities.filter(o => o.submittedToUWDate);
  const totalSubmitted = submittedOpps.length;
  const dirtyCount = submittedOpps.filter(o => o.isDirtyAtSubmission).length;
  const cleanCount = totalSubmitted - dirtyCount;
  const dirtyPct = totalSubmitted > 0 ? (dirtyCount / totalSubmitted * 100).toFixed(1) : '0.0';
  const cleanPct = totalSubmitted > 0 ? (cleanCount / totalSubmitted * 100).toFixed(1) : '0.0';
  // Calculate at-risk breakdown for App Out
  const appOutOpps = opportunities.filter(o => o.appOutDate);
  const totalAppOut = appOutOpps.length;
  const atRiskCount = appOutOpps.filter(o => o.isAtRiskAtAppOut).length;
  const onTrackCount = totalAppOut - atRiskCount;
  const atRiskPct = totalAppOut > 0 ? (atRiskCount / totalAppOut * 100).toFixed(1) : '0.0';
  const onTrackPct = totalAppOut > 0 ? (onTrackCount / totalAppOut * 100).toFixed(1) : '0.0';
  const INTERACTIVE_STEPS: FunnelStep[] = ['Submitted to UW', 'App Out'];
  const handleStepClick = (step: FunnelStep) => {
    if (INTERACTIVE_STEPS.includes(step)) {
      setSelectedStep(selectedStep === step ? null : step);
    }
  };
  return <Card className="p-0 overflow-hidden bg-white border border-[#E5E5E5] shadow-sm" noPadding data-id="element-258">
      <div className="p-4" data-id="element-259">
        <div className="flex items-center justify-between max-w-full overflow-x-auto hide-scrollbar" data-id="element-260">
          {keyMetrics.map((metric, index) => {
          const isLast = index === keyMetrics.length - 1;
          const nextMetric = !isLast ? keyMetrics[index + 1] : null;
          const stepConversion = nextMetric && metric.count > 0 ? (nextMetric.count / metric.count * 100).toFixed(0) : '0';
          const label = STEP_LABELS[metric.step] || metric.step;
          const isInteractive = INTERACTIVE_STEPS.includes(metric.step);
          const isSelected = selectedStep === metric.step;
          return <Fragment key={`num-${metric.step}`} data-id="element-261">
                <div className={clsx('flex flex-col items-center text-center px-3 py-2 rounded-lg transition-colors shrink-0', isInteractive && 'cursor-pointer hover:bg-[#F5F5F5]', isSelected && 'bg-[#FBF7E8] ring-1 ring-[#B8960C]')} onClick={() => handleStepClick(metric.step)} data-id="element-262">
                  <div className={clsx('text-[11px] font-medium mb-1 uppercase tracking-wider', isInteractive ? 'text-[#181818] border-b border-dashed border-[#CCCCCC]' : 'text-[#706E6B]')} data-id="element-263">
                    {label}
                  </div>
                  <div className={clsx('text-xl font-semibold', isSelected ? 'text-[#B8960C]' : 'text-[#181818]')} data-id="element-264">
                    {metric.count.toLocaleString()}
                  </div>
                </div>

                {!isLast && <div className="flex flex-col items-center justify-center px-1 shrink-0" data-id="element-265">
                    <ArrowRight className="h-4 w-4 text-[#CCCCCC] mb-0.5" data-id="element-266" />
                    <span className="text-[10px] font-medium text-[#706E6B] bg-[#F5F5F5] px-1.5 py-0.5 rounded-full" data-id="element-267">
                      {stepConversion}%
                    </span>
                  </div>}
              </Fragment>;
        })}
        </div>
      </div>

      {/* Detail Panels */}
      <AnimatePresence mode="wait" data-id="element-268">
        {selectedStep === 'Submitted to UW' && <motion.div key="submitted" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="border-t border-[#E5E5E5] bg-[#FAFAFA] overflow-hidden" data-id="element-269">
            <div className="p-4 max-w-3xl mx-auto" data-id="element-270">
              <div className="flex items-center justify-between mb-4" data-id="element-271">
                <div data-id="element-272">
                  <h4 className="text-sm font-semibold text-[#181818]" data-id="element-273">
                    Submission Quality
                  </h4>
                  <p className="text-xs text-[#706E6B]" data-id="element-274">
                    Breakdown of dirty vs clean files at the time of UW
                    submission
                  </p>
                </div>
                <button onClick={() => setSelectedStep(null)} className="p-1.5 text-[#706E6B] hover:bg-[#E5E5E5] rounded-md transition-colors" data-id="element-275">
                  <X className="h-4 w-4" data-id="element-276" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-277">
                <div className="bg-white p-3 rounded-lg border border-[#E5E5E5] shadow-sm" data-id="element-278">
                  <div className="flex items-center gap-2 mb-1" data-id="element-279">
                    <div className="w-2 h-2 rounded-full bg-[#04844B]" data-id="element-280" />
                    <span className="text-xs font-medium text-[#706E6B]" data-id="element-281">
                      Clean Files
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-282">
                    <span className="text-lg font-semibold text-[#181818]" data-id="element-283">
                      {cleanCount}
                    </span>
                    <span className="text-xs font-medium text-[#04844B]" data-id="element-284">
                      {cleanPct}%
                    </span>
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg border border-[#E5E5E5] shadow-sm" data-id="element-285">
                  <div className="flex items-center gap-2 mb-1" data-id="element-286">
                    <div className="w-2 h-2 rounded-full bg-[#EA001E]" data-id="element-287" />
                    <span className="text-xs font-medium text-[#706E6B]" data-id="element-288">
                      Dirty Files
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-289">
                    <span className="text-lg font-semibold text-[#181818]" data-id="element-290">
                      {dirtyCount}
                    </span>
                    <span className="text-xs font-medium text-[#EA001E]" data-id="element-291">
                      {dirtyPct}%
                    </span>
                  </div>
                </div>
              </div>

              {totalSubmitted > 0 && <div data-id="element-292">
                  <div className="flex h-2 rounded-full overflow-hidden bg-[#E5E5E5] mb-1.5" data-id="element-293">
                    <div className="h-full bg-[#04844B] transition-all duration-1000 ease-out" style={{
                width: `${cleanPct}%`
              }} data-id="element-294" />
                    <div className="h-full bg-[#EA001E] transition-all duration-1000 ease-out" style={{
                width: `${dirtyPct}%`
              }} data-id="element-295" />
                  </div>
                  <div className="flex justify-between text-[10px] text-[#706E6B] font-medium" data-id="element-296">
                    <span data-id="element-297">{cleanPct}% Clean</span>
                    <span data-id="element-298">{dirtyPct}% Dirty</span>
                  </div>
                </div>}
            </div>
          </motion.div>}

        {selectedStep === 'App Out' && <motion.div key="appout" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="border-t border-[#E5E5E5] bg-[#FAFAFA] overflow-hidden" data-id="element-299">
            <div className="p-4 max-w-3xl mx-auto" data-id="element-300">
              <div className="flex items-center justify-between mb-4" data-id="element-301">
                <div data-id="element-302">
                  <h4 className="text-sm font-semibold text-[#181818]" data-id="element-303">
                    App Out Risk Assessment
                  </h4>
                  <p className="text-xs text-[#706E6B]" data-id="element-304">
                    Breakdown of files flagged as &lsquo;At Risk&rsquo; at the
                    App Out stage
                  </p>
                </div>
                <button onClick={() => setSelectedStep(null)} className="p-1.5 text-[#706E6B] hover:bg-[#E5E5E5] rounded-md transition-colors" data-id="element-305">
                  <X className="h-4 w-4" data-id="element-306" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-307">
                <div className="bg-white p-3 rounded-lg border border-[#E5E5E5] shadow-sm" data-id="element-308">
                  <div className="flex items-center gap-2 mb-1" data-id="element-309">
                    <div className="w-2 h-2 rounded-full bg-[#04844B]" data-id="element-310" />
                    <span className="text-xs font-medium text-[#706E6B]" data-id="element-311">
                      On Track
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-312">
                    <span className="text-lg font-semibold text-[#181818]" data-id="element-313">
                      {onTrackCount}
                    </span>
                    <span className="text-xs font-medium text-[#04844B]" data-id="element-314">
                      {onTrackPct}%
                    </span>
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg border border-[#E5E5E5] shadow-sm" data-id="element-315">
                  <div className="flex items-center gap-2 mb-1" data-id="element-316">
                    <div className="w-2 h-2 rounded-full bg-[#FF9A3C]" data-id="element-317" />
                    <span className="text-xs font-medium text-[#706E6B]" data-id="element-318">
                      At Risk
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-319">
                    <span className="text-lg font-semibold text-[#181818]" data-id="element-320">
                      {atRiskCount}
                    </span>
                    <span className="text-xs font-medium text-[#B86800]" data-id="element-321">
                      {atRiskPct}%
                    </span>
                  </div>
                </div>
              </div>

              {totalAppOut > 0 && <div data-id="element-322">
                  <div className="flex h-2 rounded-full overflow-hidden bg-[#E5E5E5] mb-1.5" data-id="element-323">
                    <div className="h-full bg-[#04844B] transition-all duration-1000 ease-out" style={{
                width: `${onTrackPct}%`
              }} data-id="element-324" />
                    <div className="h-full bg-[#FF9A3C] transition-all duration-1000 ease-out" style={{
                width: `${atRiskPct}%`
              }} data-id="element-325" />
                  </div>
                  <div className="flex justify-between text-[10px] text-[#706E6B] font-medium" data-id="element-326">
                    <span data-id="element-327">{onTrackPct}% On Track</span>
                    <span data-id="element-328">{atRiskPct}% At Risk</span>
                  </div>
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </Card>;
}